import React, { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight, Inbox, LifeBuoy, Mail, MessagesSquare, Paperclip, Plus, RefreshCw, Search,
} from 'lucide-react';
import {
  AppButton,
  AppCard,
  AppCardContent,
  AppCardDescription,
  AppCardHeader,
  AppCardTitle,
  Callout,
  EmptyState,
  IconButton,
  Input,
  PageHeader,
  Paginator,
  PanelButton,
  SegmentedControl,
  StatusBadge,
} from '../ui';
import Shimmer from '../Common/Shimmer';
import { useBrandingOptional } from '../../contexts/BrandingContext';
import { getCurrentUser } from '../../utils/auth';
import NewTicketModal from './NewTicketModal';
import TicketPriority from './TicketPriority';
import useTicketList from './useTicketList';
import { CATEGORIES, audienceOf, categoryLabel, firstName, timeAgo } from './supportVocabulary';

const PAGE_SIZE = 10;

/** The three views a requester needs. `status` is what the server is asked for. */
const VIEWS = [
  { key: 'active', label: 'Open', status: 'open,in_progress', countOf: (s) => s.active },
  { key: 'done', label: 'Resolved', status: 'resolved,closed', countOf: (s) => s.resolved + s.closed },
  { key: 'all', label: 'All', status: undefined, countOf: (s) => s.total },
];

/** One request in the requester's list — the whole row is the link. */
function RequestRow({ ticket, to }) {
  const replied = ticket.awaiting === 'requester';
  return (
    <li className="tw-border-0 tw-border-t tw-border-solid tw-border-line-soft first:tw-border-t-0">
      <Link
        to={to}
        className="tw-group tw-flex tw-items-center tw-gap-4 tw-px-5 tw-py-3.5 tw-text-inherit tw-no-underline tw-transition-colors tw-focus-ring-inset hover:tw-bg-surface-hover"
      >
        <div className="tw-min-w-0 tw-flex-1">
          <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
            <span className="tw-min-w-0 tw-truncate tw-text-sm tw-font-semibold tw-text-content group-hover:tw-text-primary">
              {ticket.title}
            </span>
            {replied && <StatusBadge status="info">New reply</StatusBadge>}
          </div>
          <div className="tw-mt-1.5 tw-flex tw-items-center tw-gap-3 min-[576px]:tw-hidden">
            <StatusBadge status={ticket.status} domain="supportTicket" />
            <TicketPriority priority={ticket.priority} />
          </div>
          <p className="tw-m-0 tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-0.5 tw-text-xs tw-text-content-muted">
            <span className="tw-font-mono tw-text-content-subtle">{ticket.ticketId}</span>
            <span aria-hidden="true">·</span>
            <span>{categoryLabel(ticket.category)}</span>
            <span aria-hidden="true">·</span>
            <span>Updated {timeAgo(ticket.updatedAt || ticket.createdAt)}</span>
            {ticket.replyCount > 0 && (
              <span className="tw-inline-flex tw-items-center tw-gap-1">
                <span aria-hidden="true">·</span>
                <MessagesSquare size={13} aria-hidden="true" />
                {ticket.replyCount} {ticket.replyCount === 1 ? 'reply' : 'replies'}
              </span>
            )}
            {ticket.hasAttachment && (
              <span className="tw-inline-flex tw-items-center tw-gap-1">
                <span aria-hidden="true">·</span>
                <Paperclip size={13} aria-hidden="true" />
                <span className="tw-sr-only">Has a screenshot</span>
              </span>
            )}
          </p>
        </div>
        <div className="tw-hidden tw-shrink-0 tw-items-center tw-gap-4 min-[576px]:tw-flex">
          <TicketPriority priority={ticket.priority} />
          <StatusBadge status={ticket.status} domain="supportTicket" />
        </div>
        <ChevronRight size={18} aria-hidden="true" className="tw-shrink-0 tw-text-content-subtle group-hover:tw-text-primary" />
      </Link>
    </li>
  );
}

/**
 * The help center (`/s/support`, `/i/support`) — where a student or a trainer
 * starts a request and follows the ones they have.
 *
 * Topic cards come first because "what do I want help with" is the question
 * on arrival; the list of their requests is second because it is where they
 * return. A request with a reply waiting says so in the list.
 *
 * Props:
 *   basePath — the route this page is mounted at; a request is `${basePath}/:id`
 */
const HelpCenterPage = ({ basePath }) => {
  const user = getCurrentUser();
  const branding = useBrandingOptional();
  const audience = audienceOf(user?.usertype);
  const name = firstName(user?.name);
  const contactEmail = branding?.contactEmail || '';

  const [view, setView] = useState('active');
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [composer, setComposer] = useState({ show: false, category: '' });
  const listTitleId = useId();

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const current = VIEWS.find((v) => v.key === view) || VIEWS[0];
  const { tickets, total, totalPages, stats, loading, error, reload } = useTicketList('/support/my-tickets', {
    page,
    pageSize: PAGE_SIZE,
    status: current.status,
    search: search || undefined,
    sortBy: 'updatedAt',
  });

  const waiting = stats?.awaitingRequester || 0;
  const openComposer = (category = '') => setComposer({ show: true, category });
  const pathOf = (id) => `${basePath}/${id}`;

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      {/* The page's h1 — portalled into the top bar like every other page's. */}
      <PageHeader icon={LifeBuoy} title="Help center" />

      <section
        aria-labelledby="help-center-title"
        className="tw-rounded tw-border tw-border-solid tw-border-line tw-bg-gradient-to-br tw-from-primary/[0.10] tw-via-surface-raised tw-to-surface-raised tw-px-5 tw-py-7 tw-shadow-card min-[768px]:tw-px-8 min-[768px]:tw-py-9"
      >
        <h2 id="help-center-title" className="tw-m-0 tw-text-[1.375rem] tw-font-bold tw-leading-tight tw-tracking-tight tw-text-content min-[768px]:tw-text-[1.75rem]">
          {name ? `How can we help, ${name}?` : 'How can we help?'}
        </h2>
        <p className="tw-m-0 tw-mt-2 tw-max-w-[38rem] tw-text-sm tw-text-content-muted">
          Tell us what’s wrong or what you need. Every request gets a number, and every reply lands right here.
        </p>
        <div className="tw-mt-5 tw-flex tw-flex-wrap tw-items-center tw-gap-x-5 tw-gap-y-3">
          <AppButton onClick={() => openComposer()}>
            <Plus size={16} aria-hidden="true" /> New request
          </AppButton>
          {contactEmail && (
            <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[0.8125rem] tw-text-content-muted">
              <Mail size={15} aria-hidden="true" />
              Prefer email?
              <a href={`mailto:${contactEmail}`} className="tw-font-medium tw-text-primary tw-focus-ring tw-rounded-sm">{contactEmail}</a>
            </span>
          )}
        </div>
      </section>

      {waiting > 0 && (
        <Callout
          tone="info"
          icon={<MessagesSquare size={16} aria-hidden="true" />}
          action={view !== 'active' ? <AppButton size="xs" variant="outline" onClick={() => { setView('active'); setPage(1); }}>Show open requests</AppButton> : undefined}
        >
          {waiting === 1 ? 'One of your requests has a reply waiting for you.' : `${waiting} of your requests have replies waiting for you.`}
        </Callout>
      )}

      <section aria-labelledby="help-topics-title">
        <h2 id="help-topics-title" className="tw-m-0 tw-mb-3 tw-text-base tw-font-semibold tw-text-content">Start a request</h2>
        <ul className="tw-m-0 tw-grid tw-list-none tw-gap-3 tw-p-0 min-[576px]:tw-grid-cols-2 min-[1200px]:tw-grid-cols-4">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            return (
              <li key={c.value} className="tw-flex">
                <PanelButton className="tw-group" onClick={() => openComposer(c.value)}>
                  <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-primary/10 tw-text-primary">
                    <Icon size={20} aria-hidden="true" />
                  </span>
                  <span className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
                    <span className="tw-text-sm tw-font-semibold tw-text-content group-hover:tw-text-primary">{c.topic}</span>
                    <span className="tw-text-[0.8125rem] tw-leading-snug tw-text-content-muted">{c.blurb[audience] || c.blurb.student}</span>
                  </span>
                </PanelButton>
              </li>
            );
          })}
        </ul>
      </section>

      <div className="tw-grid tw-items-start tw-gap-6 min-[1200px]:tw-grid-cols-[minmax(0,1fr)_20rem]">
        <AppCard as="section" aria-labelledby={listTitleId}>
          <AppCardHeader className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
            <div>
              <AppCardTitle as="h2" id={listTitleId}>Your requests</AppCardTitle>
              <AppCardDescription>
                {stats ? `${stats.active} open · ${stats.total} in total` : 'Everything you’ve asked us'}
              </AppCardDescription>
            </div>
            <IconButton label="Refresh" icon={RefreshCw} size="sm" variant="outline" onClick={reload} disabled={loading} />
          </AppCardHeader>

          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-0 tw-border-y tw-border-solid tw-border-line-soft tw-bg-surface-alt tw-px-5 tw-py-2.5">
            <SegmentedControl
              label="Show requests"
              value={view}
              onChange={(next) => { setView(next); setPage(1); }}
              options={VIEWS.map((v) => ({ value: v.key, label: v.label, count: stats ? v.countOf(stats) : undefined }))}
            />
            <div className="tw-ml-auto tw-w-full min-[576px]:tw-w-64">
              <Input
                size="sm"
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by subject or number"
                aria-label="Search your requests"
                leading={<Search size={15} aria-hidden="true" />}
              />
            </div>
          </div>

          {error && !loading && (
            <div className="tw-p-5">
              <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={reload}>Try again</AppButton>}>
                Your requests couldn’t be loaded. Check your connection and try again.
              </Callout>
            </div>
          )}

          {loading && tickets.length === 0 && !error && (
            <div className="tw-flex tw-flex-col tw-gap-3 tw-p-5" aria-busy="true" aria-label="Loading your requests">
              <Shimmer height={52} count={3} />
            </div>
          )}

          {!loading && !error && tickets.length === 0 && (
            !search && stats?.total === 0 ? (
              <EmptyState
                icon={Inbox}
                title="No requests yet"
                description="When something isn’t right, start a request and we’ll pick it up from here."
                action={<AppButton onClick={() => openComposer()}><Plus size={16} aria-hidden="true" /> New request</AppButton>}
              />
            ) : (
              <EmptyState
                icon={Search}
                title={search ? 'No requests match that search' : `No ${current.label.toLowerCase()} requests`}
                description={search ? 'Try the request number, or a word from its subject.' : 'Your other requests are under All.'}
                action={search
                  ? <AppButton size="sm" variant="outline" onClick={() => setSearchInput('')}>Clear search</AppButton>
                  : <AppButton size="sm" variant="outline" onClick={() => { setView('all'); setPage(1); }}>Show all requests</AppButton>}
              />
            )
          )}

          {tickets.length > 0 && !error && (
            <ul className={`tw-m-0 tw-list-none tw-p-0 ${loading ? 'tw-opacity-60' : ''}`} aria-busy={loading}>
              {tickets.map((t) => <RequestRow key={t._id} ticket={t} to={pathOf(t._id)} />)}
            </ul>
          )}

          {totalPages > 1 && (
            <div className="tw-border-0 tw-border-t tw-border-solid tw-border-line-soft tw-px-5 tw-py-3">
              <Paginator page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE} onChange={setPage} infoLabel="requests" />
            </div>
          )}
        </AppCard>

        <aside aria-label="Tips for a faster answer" className="tw-flex tw-flex-col tw-gap-4">
          <AppCard>
            <AppCardHeader>
              <AppCardTitle as="h2">Get an answer faster</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-3 tw-p-0 tw-text-[0.8125rem] tw-text-content-muted">
                <li><span className="tw-font-semibold tw-text-content">One problem per request.</span> Each one is tracked and answered on its own.</li>
                <li><span className="tw-font-semibold tw-text-content">Say what you expected.</span> “It shows ₹0 but I paid ₹5,000” is quicker to fix than “fees are wrong”.</li>
                <li><span className="tw-font-semibold tw-text-content">Add a screenshot.</span> Copy one and paste it straight into the form — on Windows, Win + Shift + S takes one.</li>
              </ul>
            </AppCardContent>
          </AppCard>
          <AppCard>
            <AppCardHeader>
              <AppCardTitle as="h2">What happens next</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-3 tw-p-0">
                {[
                  ['We read it', 'Someone from the team picks up your request and may ask a question.'],
                  ['You hear back here', 'Replies show on the request, and this list marks the ones waiting for you.'],
                  ['Solved — or reopened', 'Mark it solved when you’re happy. If the problem comes back, reply and it reopens.'],
                ].map(([title, text], i) => (
                  <li key={title} className="tw-flex tw-gap-3">
                    <span className="tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary/10 tw-text-xs tw-font-semibold tw-text-primary" aria-hidden="true">{i + 1}</span>
                    <span className="tw-text-[0.8125rem] tw-text-content-muted">
                      <span className="tw-block tw-font-semibold tw-text-content">{title}</span>
                      {text}
                    </span>
                  </li>
                ))}
              </ol>
            </AppCardContent>
          </AppCard>
        </aside>
      </div>

      <NewTicketModal
        show={composer.show}
        onHide={() => setComposer({ show: false, category: '' })}
        audience={audience}
        initialCategory={composer.category}
        ticketPath={pathOf}
        onCreated={() => { setView('active'); setPage(1); reload(); }}
      />
    </div>
  );
};

export default HelpCenterPage;
